import React, { useState, useEffect, useRef } from "react";
import CompanyIcon from "../icons/CompanyIcon";
import CloseIcon from "../icons/CloseIcon";
import { useTranslationContext } from "../context/TranslationsProvider";
import { useEmpresaSearch } from "../hooks/useEmpresaSearch";

export default function EmpresaSelect({ empresa, setEmpresa, configuration }) {
  const { translations } = useTranslationContext();
  const t = (key, fallback) => translations[key] || fallback;
  const { options, loading, searchEmpresas } = useEmpresaSearch(configuration);
  
  const [inputValue, setInputValue]       = useState("");
  const [isOpen, setIsOpen]               = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [focusedIndex, setFocusedIndex]   = useState(-1);
  
  const inputRef    = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!empresa) {
      setSelectedOption(null);
      return;
    }
    const found = options.find(opt => opt.value === parseInt(empresa, 10));
    if (found) {
      setSelectedOption(found);
    } else {
      setSelectedOption({
        value: parseInt(empresa, 10),
        label: `Empresa ID: ${empresa}`,
        idEmpresa: empresa
      });
    }
  }, [empresa, options]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = e => {
    const value = e.target.value;
    setInputValue(value);
    setIsOpen(value.length > 0);
    setFocusedIndex(-1);
    searchEmpresas(value);
  };

  const handleOptionClick = option => {
    setEmpresa(option.value);
    setSelectedOption(option);
    setInputValue("");
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const handleInputKeyDown = e => {
    if (!isOpen || options.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex(prev =>
          prev < options.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex(prev =>
          prev > 0 ? prev - 1 : options.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && options[focusedIndex]) {
          handleOptionClick(options[focusedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    setEmpresa("");
    setSelectedOption(null);
    setInputValue("");
    setIsOpen(false);
    setFocusedIndex(-1);
    inputRef.current?.focus();
  };

  const handleControlClick = () => {
    inputRef.current?.focus();
  };

  const isValid = Boolean(empresa && selectedOption);

  return (
    <div className="empresa-select-container">
      <label className="empresa-label">
        {t("bdo-libranza-label-select-company", "¿En qué empresa trabajas?")}
      </label>

      <div className="empresa-select-dropdown" ref={dropdownRef}>
        <div
          className={`custom-select-control ${isValid ? "valid" : ""}`}
          onClick={handleControlClick}
        >
          <CompanyIcon className="company-icon" />

          {selectedOption && !inputValue ? (
            <div className="selected-value">
              {selectedOption.label}
            </div>
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              onFocus={() => inputValue && setIsOpen(true)}
              placeholder={t(
                "bdo-libranza-placeholder-select-company",
                "Busca tu empresa"
              )}
              className="search-input"
            />
          )}

          <div className="control-actions">
            {loading && <div className="loading-spinner" />}

            {selectedOption && (
              <button
                type="button"
                onClick={handleClear}
                className="clear-button only-x"
                aria-label="Limpiar selección"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        </div>

        {isOpen && (
          <div className="custom-select-menu">
            {options.length > 0 ? (
              options.map((opt, idx) => (
                <div
                  key={opt.value}
                  className={`custom-select-option ${
                    idx === focusedIndex ? "focused" : ""
                  }`}
                  onClick={() => handleOptionClick(opt)}
                  onMouseEnter={() => setFocusedIndex(idx)}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="no-options-message">
                {inputValue
                  ? "No se encontraron empresas"
                  : "Escribe para buscar"}
              </div>
            )}
          </div>
        )}
      </div>

      <p className="empresa-helper">
        {t(
          "bdo-libranza-helper-company",
          "Asegúrate que tu empresa tenga un convenio con nosotros"
        )}
      </p>
    </div>
  );
}
